import { useCallback, useEffect, useState } from 'react'
import {
  api, ApiFailure,
  REMINDER_KIND_LABEL, REMINDER_STATUS_LABEL,
  type AuditEntry, type Reminder, type ReminderStatus,
} from '../api'

function kb(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime()
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (s < 60) return `${s} s ago`
  if (s < 3600) return `${Math.floor(s / 60)} min ago`
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`
  return `${Math.floor(s / 86400)} d ago`
}

/**
 * The admin ops screen. Backups on top because they are the daily-hygiene
 * action; the audit viewer below because it is the "what happened" tool an
 * admin reaches for when reception says something looks off.
 */
export default function Admin() {
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error' | 'warn'; text: string } | null>(null)

  const [entries, setEntries] = useState<AuditEntry[]>([])
  const [filter, setFilter] = useState({ entity: '', userId: '', from: '', to: '' })
  const [total, setTotal] = useState(0)
  const [expanded, setExpanded] = useState<string | null>(null)

  const [reminders, setReminders] = useState<Reminder[]>([])
  const [reminderCounts, setReminderCounts] = useState<Record<string, number>>({})
  const [reminderProvider, setReminderProvider] = useState<string>('log')
  const [reminderStatus, setReminderStatus] = useState<ReminderStatus | ''>('')

  const loadAudit = useCallback(async () => {
    const p = new URLSearchParams({ limit: '50' })
    for (const [k, v] of Object.entries(filter)) if (v) p.set(k, v)
    const r = await api.get<{ entries: AuditEntry[]; total: number }>(`/api/reports/audit?${p.toString()}`)
    setEntries(r.entries); setTotal(r.total)
  }, [filter])

  useEffect(() => { void loadAudit().catch((e) => setMsg({ kind: 'error', text: (e as Error).message })) }, [loadAudit])

  const loadReminders = useCallback(async () => {
    const p = new URLSearchParams({ limit: '100' })
    if (reminderStatus) p.set('status', reminderStatus)
    const r = await api.get<{ reminders: Reminder[]; counts: Record<string, number>; provider: string }>(
      `/api/reminders?${p.toString()}`,
    )
    setReminders(r.reminders); setReminderCounts(r.counts); setReminderProvider(r.provider)
  }, [reminderStatus])
  useEffect(() => { void loadReminders().catch((e) => setMsg({ kind: 'error', text: (e as Error).message })) }, [loadReminders])

  async function generateReminders() {
    setBusy(true); setMsg(null)
    try {
      const r = await api.post<{ followUps: number; appointments: number }>(
        '/api/reminders/generate', { horizonDays: 3 },
      )
      setMsg({ kind: 'ok', text: `Queued ${r.followUps} follow-ups and ${r.appointments} appointment reminders.` })
      await loadReminders()
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not generate.' })
    } finally { setBusy(false) }
  }

  async function sendDueReminders() {
    setBusy(true); setMsg(null)
    try {
      const r = await api.post<{ sent: number; failed: number }>('/api/reminders/send-due')
      setMsg({ kind: r.failed > 0 ? 'warn' : 'ok', text: `Sent ${r.sent}; ${r.failed} failed.` })
      await loadReminders()
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not send.' })
    } finally { setBusy(false) }
  }

  async function cancelReminder(id: string) {
    if (!confirm('Cancel this pending reminder?')) return
    try {
      await api.post(`/api/reminders/${id}/cancel`)
      await loadReminders()
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'Could not cancel.' })
    }
  }





  return (
    <>
      <div className="page-head">
        <h2>Admin</h2>
        <p>Reminders and the audit log for this clinic.</p>
      </div>

      {msg && <div className={`msg ${msg.kind}`}>{msg.text}</div>}

      <div className="panel">
        <h3 style={{ margin: 0 }}>Backups</h3>
        <p className="panel-note">
          A backup holds every clinic on this server, so backups are taken and restored by the platform
          administrator, not from inside one clinic. Ask them for a copy of your clinic&rsquo;s data.
        </p>
      </div>

      <div className="panel">
        <div className="records-views">
          <h3 style={{ margin: 0 }}>Reminders</h3>
          <span className="spacer" />
          <button className="secondary" disabled={busy} onClick={() => void generateReminders()}>
            Generate for next 3 days
          </button>
          <button disabled={busy} onClick={() => void sendDueReminders()}>Send due now</button>
        </div>
        <p className="panel-note">
          Provider: <code>{reminderProvider}</code>{' '}
          {reminderProvider === 'log' && (
            <span>· messages are recorded to the server log rather than sent. Plug in a real gateway to switch this.</span>
          )}
        </p>
        <div className="records-views" style={{ marginTop: 6 }}>
          {(['', 'pending', 'sent', 'failed', 'cancelled'] as const).map((s) => (
            <button
              key={s || 'all'}
              className={reminderStatus === s ? 'chip is-on' : 'chip'}
              onClick={() => setReminderStatus(s)}
            >
              {s === '' ? 'All' : REMINDER_STATUS_LABEL[s]}
              {s && reminderCounts[s] != null && <span className="count">{reminderCounts[s]}</span>}
            </button>
          ))}
        </div>
        <div className="table-wrap" style={{ marginTop: 10 }}>
          <table className="records-table">
            <thead>
              <tr>
                <th>Scheduled</th><th>Kind</th><th>Patient</th><th>Body</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {reminders.length === 0 && (
                <tr><td colSpan={6}><div className="empty">Nothing queued.</div></td></tr>
              )}
              {reminders.map((r) => (
                <tr key={r.id}>
                  <td className="nowrap">{new Date(r.scheduledFor).toLocaleString()}</td>
                  <td className="nowrap">{REMINDER_KIND_LABEL[r.kind]}</td>
                  <td>
                    <div className="rec-name">{r.patientName}</div>
                    <div className="rec-code">{r.patientCode} · {r.toMobile}</div>
                  </td>
                  <td>{r.body}{r.errorMessage && <div className="rec-code" style={{ color: 'var(--danger)' }}>{r.errorMessage}</div>}</td>
                  <td><span className={`pill ${r.status === 'sent' ? 'on' : r.status === 'failed' ? 'off' : ''}`}>{REMINDER_STATUS_LABEL[r.status]}</span></td>
                  <td className="nowrap">
                    {r.status === 'pending' && <button className="link" onClick={() => void cancelReminder(r.id)}>Cancel</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel">
        <div className="records-views">
          <h3 style={{ margin: 0 }}>Audit log</h3>
          <span className="spacer" />
          <span className="panel-note" style={{ margin: 0 }}>Showing {entries.length} of {total}</span>
        </div>
        <div className="records-filters" style={{ marginTop: 10 }}>
          <input placeholder="Entity (patients, visits, prescriptions…)" value={filter.entity}
            onChange={(e) => setFilter((f) => ({ ...f, entity: e.target.value }))} />
          <input placeholder="User id" value={filter.userId}
            onChange={(e) => setFilter((f) => ({ ...f, userId: e.target.value }))} />
          <input type="date" value={filter.from}
            onChange={(e) => setFilter((f) => ({ ...f, from: e.target.value }))} />
          <input type="date" value={filter.to}
            onChange={(e) => setFilter((f) => ({ ...f, to: e.target.value }))} />
        </div>
        <div className="audit-list">
          {entries.length === 0 && <div className="empty">No entries match.</div>}
          {entries.map((e) => (
            <div key={e.id} className="audit-row">
              <button
                type="button"
                className="audit-head"
                onClick={() => setExpanded((k) => (k === e.id ? null : e.id))}
              >
                <span className="audit-time">{new Date(e.createdAt).toLocaleString()}</span>
                <span className="audit-what">
                  <strong>{e.action}</strong> {e.entity}
                  {e.entityId && <span className="rec-code"> · {e.entityId.slice(0, 8)}</span>}
                </span>
                <span className="audit-who">
                  {e.userFullName ?? e.username ?? 'system'}
                  {e.ip && <span className="panel-note"> · {e.ip}</span>}
                </span>
              </button>
              {expanded === e.id && (
                <div className="audit-body">
                  {e.before != null && (
                    <div><div className="audit-label">Before</div><pre>{JSON.stringify(e.before, null, 2)}</pre></div>
                  )}
                  {e.after != null && (
                    <div><div className="audit-label">After</div><pre>{JSON.stringify(e.after, null, 2)}</pre></div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

    </>
  )
}
